import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined"
import Box from "@mui/material/Box"
import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { useLinkedStyles } from "../../../../hooks/useLinkedStyles"
import ExpandableSection from "../../../common/ExpandableSection"
import IndependentToggle from "../../../common/PropertyTools/IndependentToggle"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"

interface Props {
    object: AnySceneObjectT
    handleSetValue: (property: any, value: any) => void
}

/** The four corners in reading order, with the label of each one's own field. */
const CORNERS = [
    { property: "borderTopLeftRadius", label: "top left" },
    { property: "borderTopRightRadius", label: "top right" },
    { property: "borderBottomLeftRadius", label: "bottom left" },
    { property: "borderBottomRightRadius", label: "bottom right" },
]
const CORNER_PROPERTIES = CORNERS.map((corner) => corner.property)

/**
 * The whole object's look, as Figma's Appearance: its opacity and its corner radius side by
 * side. One radius field covers all four corners; the toggle at the end splits it into the
 * four (see useLinkedStyles).
 *
 * The radius is disabled where the object has no corners to round — a line or a text has no
 * radius styles, and an ellipse's are its shape (50 %), not something to edit.
 */
const Appearance = ({ object, handleSetValue }: Props) => {
    const opacity: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "opacity"
    )
    const radius = useLinkedStyles(object, CORNER_PROPERTIES, handleSetValue)
    const hasRadius = radius.styles.length > 0 && object.type !== "ellipse"

    if (!opacity && !hasRadius) return <></>

    const opacityPercent = opacity ? Math.round(Number(opacity.value) * 100) : ""

    /** Shown in %, stored as 0–1 like the CSS it renders to. */
    const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = String(e.target.value).trim()
        const percent = Number(text)
        if (text === "" || !Number.isFinite(percent) || percent === opacityPercent) return
        handleSetValue("opacity", Math.max(0, Math.min(100, percent)) / 100)
    }

    return (
        <ExpandableSection
            expanded={true}
            title={"Appearance"}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    {/* the corners toggle sits in the narrow column at the end, as in Figma */}
                    <PropertyRow
                        gutter={
                            <IndependentToggle
                                title="Independent corners"
                                icon={<CropFreeOutlinedIcon fontSize="small" />}
                                active={hasRadius && radius.independent}
                                onClick={radius.toggleIndependent}
                                disabled={!hasRadius}
                            />
                        }
                    >
                        <Box display="flex" gap={1}>
                            <Box flex={1} minWidth={0}>
                                {opacity && (
                                    <PrefixedInput
                                        name="opacity"
                                        value={opacityPercent}
                                        label="opacity"
                                        tooltip="Opacity (%)"
                                        handleOnChange={handleOpacityChange}
                                        min={0}
                                        max={100}
                                        endAdornment="%"
                                    />
                                )}
                            </Box>
                            <Box flex={1} minWidth={0}>
                                <PrefixedInput
                                    name="borderRadius"
                                    value={hasRadius ? radius.sharedValue : ""}
                                    placeholder={hasRadius && radius.isMixed ? "Mixed" : undefined}
                                    label={`radius (${radius.unit})`}
                                    tooltip={`Corner radius (${radius.unit})`}
                                    handleOnChange={radius.handleAllChange}
                                    min={0}
                                    disabled={!hasRadius}
                                />
                            </Box>
                        </Box>
                    </PropertyRow>
                    {hasRadius && radius.independent && (
                        <PropertyRow>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                {radius.styles.map(({ property, style }) => {
                                    const label = `${
                                        CORNERS.find((corner) => corner.property === property)
                                            ?.label
                                    } (${style.unit ?? radius.unit})`
                                    return (
                                        <Box key={property} minWidth={0}>
                                            <PrefixedInput
                                                name={property}
                                                value={style.value}
                                                label={label}
                                                tooltip={label}
                                                handleOnChange={radius.handleOneChange}
                                                min={0}
                                            />
                                        </Box>
                                    )
                                })}
                            </Box>
                        </PropertyRow>
                    )}
                </Box>
            }
        />
    )
}

export default Appearance
